/**
 * Subscriptions Service
 * Abstraction layer for subscription management API calls
 */
import { api } from './api'

// Real 6-value account_status (single source of truth, per accountStatus.service.js
// on the backend) — no longer collapsed into a lossy 4-way mapping, so admin can
// actually distinguish e.g. Suspension Scheduled from Suspended.
export const ACCOUNT_STATUS_LABELS = {
    active_trial: 'Active Trial',
    active_trial_end_scheduled: 'Trial Ending (Not Converting)',
    expired_trial: 'Trial Expired',
    active_paid: 'Active',
    active_paid_suspension_scheduled: 'Suspension Scheduled',
    suspended: 'Suspended',
}

// Mirrors backend/constants/suspensionReasons.js (SUSPENSION_REASON_LABELS)
export const SUSPENSION_REASON_LABELS = {
    not_enough_opportunities: 'Not enough relevant opportunities',
    too_expensive: 'Too expensive',
    no_longer_tendering: 'No longer tendering',
    temporary_financial_reasons: 'Temporary financial reasons',
    technical_usability_issue: 'Technical or usability issue',
    organisation_closed: 'Organisation closed',
    switched_to_another_service: 'Switched to another service',
    other: 'Other',
}

// "Other: <free text>" for the free-text case; null when no reason was given.
export function formatSuspensionReason(reason, reasonOther) {
    if (!reason) return null
    const label = SUSPENSION_REASON_LABELS[reason] || reason
    return reason === 'other' && reasonOther ? `${label}: ${reasonOther}` : label
}

const FULLY_ACTIVE =['active_trial', 'active_trial_end_scheduled', 'active_paid', 'active_paid_suspension_scheduled']

export const subscriptionsService = {
    async getAll() {
        const res = await api.get('/admin/organisations')
        if (!res.success) throw new Error('Failed to fetch subscriptions')

        return res.data.map(org => ({
            id: org.id,
            organisation: org.name,
            plan: `TenderCentriX Discovery – ${org.billing_interval === 'year' ? 'Annual' : 'Monthly'}`,
            status: org.account_status || 'active_trial',
            statusLabel: ACCOUNT_STATUS_LABELS[org.account_status] || org.account_status,
            isFullyActive: FULLY_ACTIVE.includes(org.account_status),
            rawStatus: org.subscription_status,
            // Live Stripe amount for this org's billing interval — resolved server-side
            // in adminController.getOrganisations, so it can't drift from what's charged.
            amount: org.amount ?? 0,
            currency: org.currency || 'gbp',
            billingInterval: org.billing_interval || 'month',
            nextBilling: org.subscription_end || org.trial_end || org.created_at,
            startedAt: org.subscription_start || org.created_at,
            cancelledAt: org.cancelled_at || null,
            accessUntil: org.subscription_end || null,
            suspensionEffectiveAt: org.suspension_effective_at || null,
            suspensionReason: formatSuspensionReason(org.suspension_reason, org.suspension_reason_other),
            stripeCustomerId: org.stripe_customer_id,
            isInternal: !!org.is_internal,
        }))
    },

    async getById(id) {
        throw new Error('Not implemented on backend API')
    },

    async cancel(id) {
        throw new Error('Not implemented on backend API')
    },
}
